/** Sharing-policy contracts and system defaults consumed by settings and read-time sharing evaluation. */
import { Entity } from '../entity';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { ActivitySource } from '../shared/activity.sharing';
import { Lex, LexoRank } from '../shared/lexorank';
import { Source } from '../../enums/enum.source';
import { v4 } from 'uuid';

export enum JobSharingPolicySource {

    /* These are items from the client portal */
    Customer = 'Customer',

    /* These are items from the contractor portal and field app */
    Field = 'Field',

    /* These are items from the Tenant and Manager App */
    Office = 'Office',

    /** Generated job tasks retain their Service Plan origin independently of their application source. */
    ServicePlan = 'Service Plan',

    /* These are items that are not known - Pro Mode*/
    Unknown = 'Unknown'
}

export interface JobSharingPolicyConditions {
    _id?: string;
    entity: ActivityEntityName;
    source: JobSharingPolicySource,
    customers: Entity[];
    tags: Entity[];
}

export interface JobSharingPolicyActions {
    _id?: string;
    shareWithField: boolean;
    shareWithCustomer: boolean;
    markSensitive: boolean;
}

export interface JobSharingPolicy extends Entity {
    name: string;
    conditions: JobSharingPolicyConditions;
    actions: JobSharingPolicyActions;
    sortOrder: Lex;
}

export const JobSharingPolicySources = Object.values(JobSharingPolicySource).filter(x => x !== JobSharingPolicySource.Unknown);

export const toJobSharingPolicySource = (source: Source | ActivitySource): JobSharingPolicySource => {

    switch (source) {

        case Source.ClientPortal:
        case ActivitySource.ClientPortal:
            return JobSharingPolicySource.Customer;

        case Source.ContractorPortal:
        case Source.Mobile:
        case Source.Field:
        case ActivitySource.ContractorPortal:
        case ActivitySource.Mobile:
            return JobSharingPolicySource.Field;

        case Source.Import:
        case Source.Manager:
        case Source.Web:
        case ActivitySource.Tenant:
            return JobSharingPolicySource.Office;

        default:
            return JobSharingPolicySource.Office;
    }
}

export const notSharedPolicyAction = () => {
    return {
        _id: v4(),
        shareWithCustomer: false,
        shareWithField: false,
        markSensitive: false
    } as JobSharingPolicyActions;
}

/** Shared display and evaluation order: the task-specific system rule precedes generic source defaults. */
export const defaultSharingPolicies: JobSharingPolicy[] = [
    Object.freeze({
        _id: '2',
        active: true,
        name: 'Default Service Plan Task Sharing',
        sortOrder: LexoRank.first(),
        conditions: {
            source: JobSharingPolicySource.ServicePlan,
            customers: [],
            entity: ActivityEntityName.Task,
            tags: [],
        },
        actions: {
            shareWithCustomer: true,
            shareWithField: true,
            markSensitive: false,
        },
    }),
    Object.freeze({
        _id: '0',
        active: true,
        name: 'Default Customer Sharing',
        sortOrder: LexoRank.first(),
        conditions: {
            source: JobSharingPolicySource.Customer,
            customers: [],
            entity: null,
            tags: [],
        },
        actions: {
            shareWithCustomer: true,
            shareWithField: false,
            markSensitive: false,
        }
    }),
    Object.freeze({
        _id: '1',
        active: true,
        name: 'Default Field Sharing',
        sortOrder: LexoRank.first(),
        conditions: {
            source: JobSharingPolicySource.Field,
            customers: [],
            entity: null,
            tags: [],
        },
        actions: {
            shareWithCustomer: false,
            shareWithField: true,
            markSensitive: false,
        }
    })
];
